import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import type { DocumentExample } from "./types";

// The examples are read, not collected: a customer wants to see what a good pay plan looks
// like without a file landing in their downloads folder and a text editor opening on top
// of the flow. Fetched from the same origin rather than duplicated here, so the file the
// download link hands over is the editable version of the file they were shown.
export const ExamplePreviewModal = ({
  example,
  onClose,
}: {
  example: DocumentExample | null;
  onClose: () => void;
}) => {
  const href = example?.href;
  const downloadHref = example?.downloadHref ?? href;

  return (
    <Dialog open={!!example} onOpenChange={(open) => !open && onClose()}>
      {/* Close to full screen: the point of opening an example is to read it, and a
          document squeezed into a dialog-sized box is not reading. The grid rows keep the
          header fixed and hand every remaining pixel to the document. */}
      <DialogContent
        className="grid h-[90vh] max-h-none w-[94vw] max-w-[1500px] grid-rows-[auto_1fr] gap-0 overflow-hidden rounded-xl"
        description="An example of the kind of document Volca needs."
      >
        <DialogHeader>
          <Stack
            direction="row"
            gap="lg"
            align="center"
            justify="between"
            fullWidth
            className="pr-10"
          >
            <DialogTitle>{example?.label}</DialogTitle>
            {downloadHref && (
              <a
                href={downloadHref}
                download
                className="shrink-0 text-sm text-primary underline-offset-4 hover:underline"
              >
                {/* The Word file when there is one: a plan is something they edit, and a
                    rendered copy is not editable. */}
                {downloadHref.endsWith(".docx")
                  ? "Download the Word version"
                  : "Download this example"}
              </a>
            )}
          </Stack>
        </DialogHeader>

        <Stack
          align="stretch"
          fullWidth
          className="min-h-0 overflow-hidden px-6 pb-5"
        >
          {href && (
            <iframe
              // navpanes=0 opens on the page itself rather than behind a strip of
              // thumbnails, which is most of the width gone before anything is read.
              src={`${href}#view=FitH&navpanes=0&pagemode=none`}
              title={example?.label ?? "Example document"}
              className="min-h-0 w-full flex-1 rounded-md border border-stroke-tertiary"
            />
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
